import { useState } from "react";
import { motion } from "framer-motion";
import { Send, Instagram } from "lucide-react";
import { useLang } from "../../lib/lang";
import { LINKS, MATERIALS, SERVICE_COLORS, waLink } from "../../lib/content";
import { SectionHead, Reveal } from "./Reveal";

export const TikTokIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className}><path d="M16.6 5.82A4.28 4.28 0 0 1 15.54 3h-3.09v12.4a2.59 2.59 0 1 1-2.59-2.59c.27 0 .53.04.77.12V9.77a5.7 5.7 0 1 0 4.91 5.63V9.01a7.35 7.35 0 0 0 4.3 1.38V7.3a4.3 4.3 0 0 1-3.24-1.48Z" /></svg>
);

const Chip = ({ active, onClick, children, color, testid }) => (
  <button data-testid={testid} onClick={onClick} style={active ? { background: color, borderColor: color } : {}}
    className={`rounded-full px-4 py-2 text-sm border transition-colors duration-300 ${active ? "text-white" : "border-white/15 text-slate-300 hover:text-white hover:border-white/40"}`}>
    {children}
  </button>
);

const Label = ({ children }) => <p className="font-mono text-xs uppercase tracking-widest text-slate-400 mb-3">{children}</p>;

export const Order = () => {
  const { t } = useLang();
  const [service, setService] = useState("figure");
  const [material, setMaterial] = useState("");
  const [qty, setQty] = useState(1);
  const [note, setNote] = useState("");
  const sTitle = t.services.find((s) => s.key === service)?.title;
  const msg = `${t.waGreeting}\n\n• ${t.orderService}: ${sTitle}\n• ${t.orderMaterial}: ${material || t.orderMaterialUnsure}\n• ${t.orderQty}: ${qty}${note ? `\n• ${note}` : ""}`;

  return (
    <section id="order" className="relative overflow-hidden hex-bg border-t border-white/10">
      <div className="absolute -bottom-40 left-1/3 w-[600px] h-[600px] rounded-full bg-violet-600/25 blur-[160px]" />
      <div className="relative mx-auto max-w-[1400px] px-5 lg:px-10 py-24 lg:py-36 grid lg:grid-cols-12 gap-10 lg:gap-14">
        <div className="lg:col-span-7">
          <SectionHead eyebrow={t.orderEyebrow} title={t.orderTitle} testid="order-title" />
          <Reveal className="space-y-8">
            <div><Label>{t.orderService}</Label><div className="flex flex-wrap gap-2">
              {t.services.map((s) => <Chip key={s.key} testid={`order-service-${s.key}`} active={service === s.key} color={SERVICE_COLORS[s.key]} onClick={() => setService(s.key)}>{s.title}</Chip>)}
            </div></div>
            <div><Label>{t.orderMaterial}</Label><div className="flex flex-wrap gap-2">
              {[...MATERIALS.map((m) => m.name), ""].map((m) => <Chip key={m || "unsure"} testid={`order-material-${m ? m.toLowerCase().replace("+", "plus") : "unsure"}`} active={material === m} color="#38BDF8" onClick={() => setMaterial(m)}>{m || t.orderMaterialUnsure}</Chip>)}
            </div></div>
            <div><Label>{t.orderQty}</Label>
              <div className="inline-flex items-center rounded-full border border-white/15">
                <button data-testid="order-qty-minus" onClick={() => setQty(Math.max(1, qty - 1))} className="w-11 h-11 text-white text-xl hover:text-sky-300">−</button>
                <span data-testid="order-qty-value" className="w-10 text-center font-mono text-white">{qty}</span>
                <button data-testid="order-qty-plus" onClick={() => setQty(qty + 1)} className="w-11 h-11 text-white text-xl hover:text-sky-300">+</button>
              </div>
            </div>
            <div><Label>{t.orderNote}</Label>
              <textarea data-testid="order-note-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder={t.orderNotePh} rows={3}
                className="w-full rounded-2xl bg-[#13182E] border border-white/10 p-4 text-white placeholder:text-slate-500 focus:outline-none focus:border-sky-300 transition-colors duration-300 resize-none" />
            </div>
          </Reveal>
        </div>
        <Reveal delay={0.15} className="lg:col-span-5 lg:pt-36">
          <div className="rounded-[26px] border border-white/10 bg-[#13182E]/80 backdrop-blur-xl p-6 lg:p-8 lg:sticky lg:top-28">
            <Label>{t.orderPreview}</Label>
            <motion.pre key={msg} initial={{ opacity: 0.4 }} animate={{ opacity: 1 }} data-testid="order-message-preview" className="whitespace-pre-wrap font-body text-sm text-slate-200 rounded-2xl rounded-tl-sm bg-[#0B5C4A]/40 border border-emerald-400/20 p-4 leading-relaxed">{msg}</motion.pre>
            <a href={waLink(msg)} target="_blank" rel="noreferrer" data-testid="order-send-whatsapp-button" className="mt-6 flex items-center justify-center gap-3 rounded-full bg-[#FF2E4D] py-4 font-semibold text-white shadow-[0_0_40px_rgba(255,46,77,0.45)] hover:bg-[#ff4d66] transition-colors duration-300">
              <Send className="w-5 h-5" /> {t.orderSend}
            </a>
            <p className="mt-6 text-sm text-slate-400">{t.orderOr}</p>
            <div className="mt-3 grid grid-cols-2 gap-3">
              <a href={LINKS.instagram} target="_blank" rel="noreferrer" data-testid="order-instagram-button" className="flex items-center justify-center gap-2 rounded-full border border-white/15 py-3 text-white hover:border-pink-400 hover:text-pink-300 transition-colors duration-300"><Instagram className="w-4 h-4" /> Instagram</a>
              <a href={LINKS.tiktok} target="_blank" rel="noreferrer" data-testid="order-tiktok-button" className="flex items-center justify-center gap-2 rounded-full border border-white/15 py-3 text-white hover:border-sky-300 hover:text-sky-300 transition-colors duration-300"><TikTokIcon className="w-4 h-4" /> TikTok</a>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
